import React, {useContext, useEffect, useRef, useState} from 'react';

import {
	AuthBase,
	QUERY_PARAMS_TYPE,
	WithArlLogin,
	WithAnonymousLogin,
} from '@deezer/auth';

import config from 'src/config/config.json';
import {ERROR_NAME} from './types';
import {useURLParams} from './URLParamsProvider';

const customFetch: typeof fetch = (url, init = {}) => {
	init.credentials = 'include';
	return globalThis.fetch(url, init);
};

const Auth = WithAnonymousLogin(WithArlLogin(AuthBase));
const authInstance = new Auth({
	tokenOutputType: QUERY_PARAMS_TYPE.PAYLOAD,
	refreshTokenOutputType: QUERY_PARAMS_TYPE.COOKIE,
	host: config.host_authent,
	customFetch,
});
type AuthProviderValue = [
	status: {
		authStatus: AUTH_STATUS;
	},
	authMethods: {
		getToken: () => Promise<string>;
		invalidateCurrentToken: typeof authInstance.invalidateCurrentToken;
		logout: typeof authInstance.logout;
	},
];

const AuthContext = React.createContext<AuthProviderValue | void>(undefined);

const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({
	children,
}) => {
	const value = useProviderAuth();

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;

export const useAuth = (): AuthProviderValue => {
	const authContext = useContext(AuthContext);

	if (!authContext) {
		throw new Error('useAuthContext must be used whithin a AuthContext');
	}

	return authContext;
};

// Provider hook that creates auth object and handles state
function useProviderAuth(): AuthProviderValue {
	const [authState, setAuthState] = useState<AUTH_STATUS>(AUTH_STATUS.LOADING);
	const {platform, arl, userId} = useURLParams();
	const getTokenPromiseRef = useRef<Promise<string> | null>();

	// Initial check of user logged status
	// TODO Factorise with our `getToken` below once the token contains its type (anonymous or user)
	useEffect(() => {
		const setAuthStatus = async () => {
			authInstance.invalidateCurrentToken();

			const tokenPromise = authInstance
				.getToken()
				.then((value) => {
					setAuthState(AUTH_STATUS.LOGGED_AS_USER);
					return value ?? '';
				})
				.catch(() => {
					return (
						arl && userId
							? authInstance.loginWithArl(arl, userId)
							: authInstance.loginWithArl()
					)
						.then((value) => {
							setAuthState(AUTH_STATUS.LOGGED_AS_USER);
							return value ?? '';
						})
						.catch((error: {name: string; message: string; stack: string}) => {
							if (error.name === ERROR_NAME.UNLOGGED) {
								return authInstance.loginAsAnonymous().then((value) => {
									setAuthState(AUTH_STATUS.LOGGED_AS_ANONYMOUS);
									return value ?? '';
								});
							}
							return error.name ?? '';
						});
				})
				.finally(() => {
					getTokenPromiseRef.current = null;
				});

			getTokenPromiseRef.current = tokenPromise;
		};

		setAuthState(AUTH_STATUS.LOADING);
		setAuthStatus();
	}, [platform, arl, userId]);

	const getToken = async (): Promise<string> => {
		// Avoid multiple api calls when calling getToken multiple times
		if (getTokenPromiseRef.current) {
			return getTokenPromiseRef.current;
		}

		const tokenPromise = authInstance
			.getToken()
			.then((value) => {
				return value ?? '';
			})
			.catch(async () => {
				return (
					arl && userId
						? authInstance.loginWithArl(arl, userId)
						: authInstance.loginWithArl()
				)
					.then((value) => {
						setAuthState(AUTH_STATUS.LOGGED_AS_USER);
						return value ?? '';
					})
					.catch((error: {name: string; message: string; stack: string}) => {
						if (error.name === ERROR_NAME.UNLOGGED) {
							return authInstance.loginAsAnonymous().then((value) => {
								setAuthState(AUTH_STATUS.LOGGED_AS_ANONYMOUS);
								return value ?? '';
							});
						}
						return error.name ?? '';
					});
			})
			.finally(() => {
				getTokenPromiseRef.current = null;
			});

		getTokenPromiseRef.current = tokenPromise;

		return tokenPromise;
	};

	const logout: typeof authInstance.logout = async () => {
		setAuthState(AUTH_STATUS.LOADING);
		await authInstance.logout();
		await getToken();
	};

	const invalidateCurrentToken: typeof authInstance.invalidateCurrentToken =
		() => {
			authInstance.invalidateCurrentToken();
		};

	// Return the user logged status and auth methods
	return [
		{authStatus: authState},
		{
			getToken,
			invalidateCurrentToken,
			logout,
		},
	];
}

export enum AUTH_STATUS {
	LOADING,
	LOGGED_AS_USER,
	LOGGED_AS_ANONYMOUS,
}
